/*
 * Calculation method versions.
 *
 * Saved results live in localStorage indefinitely, so a result computed by an
 * older, wrong method can outlive the fix. Each calculator carries its own
 * version and a note describing what changed, so a stale saved result can be
 * flagged specifically rather than blanket-warning every stored figure.
 *
 * Per-calculator rather than one global version on purpose: bumping a single
 * app-wide number would flag every saved result whenever any one calculator
 * changed, and a warning that always fires is a warning nobody reads.
 *
 * Rules:
 *   - A calculator absent from this map is version 1 and has never changed.
 *   - An entry saved before stamping existed has no calcVersion; treat it as
 *     version 1. It is stale only if its calculator has since moved past 1.
 *   - Bump the version and add a `revisions` entry in the SAME commit that
 *     changes a formula, constant or unit. The note is read by operators, so
 *     say what changed and which direction the old numbers were wrong.
 */

import { parseNumberInput, legacyParseNumber, INPUT_READER_VERSION } from './numberInput';

export const CALC_VERSIONS = {
  feedRate: {
    version: 2,
    revisions: {
      2:
        'TAN production is now derived from feed protein via Timmons & Ebeling eq. 3.9b (feed × protein% × 0.092) whenever a protein figure is entered, falling back to the 3% of feed rule of thumb when it is left blank. The two methods agree only near 32.6% protein: the 3% rule under-predicts TAN by about 7% at 35% protein, 27% at 45% and 35% at 50%, which is the salmonid and marine range. Results from the previous version used the 3% rule unconditionally and may therefore undersize the implied TAN load it reports.',
    },
  },
  /*
   * Removed calculators stay listed. A saved result from a calculator that was
   * withdrawn for being wrong still sits in someone's project, and needs to be
   * flagged as superseded rather than silently reading as current.
   */
  flowRate: {
    version: 2,
    revisions: {
      2:
        'This calculator has been removed. It derived flow from diameter and velocity, which is backwards for design work — flow is normally the known quantity. ' +
        'Use Pipe Sizing instead: it gives required bore from flow and target velocity, and the resulting velocity at a chosen bore.',
    },
  },
  settlingBasin: {
    version: 2,
    revisions: {
      2:
        'Default surface overflow rate corrected from 0.8 to 2.5 m/h and the guidance band from 0.5–1.5 to 1.67–3.33 m/h, per Stechey & Trudell 1990 (40–80 m³/m²/day for off-line basins) in Timmons & Ebeling Ch.5. ' +
        'Basins sized on the old band are roughly 2–3× larger than the cited design range requires, for no measured gain in solids removal. ' +
        'The formula is unchanged, so a saved result is still arithmetically correct for the overflow rate it was given — it is the recommended rate that was wrong.',
    },
  },
  tankVolume: {
    version: 2,
    revisions: {
      2:
        'The capsule shape ignored the filled height and always reported 80% of total volume. Filled volume is now solved properly by region (spherical cap, cylindrical slice, top cap). ' +
        'Any capsule filled volume from the previous version was a fixed 80% and bore no relation to the height entered. Total volume was correct and is unchanged. ' +
        'A filled height above the tank height is now rejected instead of silently over-reporting.',
    },
  },
  pumpHead: {
    version: 3,
    revisions: {
      3:
        'Pipe diameter is now entered in millimetres, not metres, matching Pipe Sizing and Head Loss and how pipe is actually specified. ' +
        'A saved result that correctly used metres — a 90 mm pipe entered as 0.09 — will read as 0.09 mm if re-run, wrong by a factor of a thousand; re-enter the diameter in millimetres (90, not 0.09). ' +
        'Pump efficiency is now an input defaulting to 0.75 rather than a fixed 0.75, and a diameter outside the plausible aquaculture range is flagged. Head, velocity and motor figures are otherwise unchanged.',
      2:
        'Motor sizing applied pump efficiency twice, inflating the requirement by 1/0.75 and reporting roughly a frame size too large — 2 HP where 0.78 HP shaft power needs a 1 HP motor. ' +
        'Efficiency is now applied once, to get shaft power from hydraulic power. Head, velocity and friction figures are unaffected; only the motor line changes. ' +
        'Motor size is now taken from the standard HP frame series rather than rounded up to a whole number.',
    },
  },
  pipeVolume: {
    version: 2,
    revisions: {
      2:
        'Pipe diameter is now entered in millimetres, not metres. ' +
        'A saved result that correctly used metres — a 90 mm pipe entered as 0.09 — will read as 0.09 mm if re-run, wrong by a factor of a thousand, and since volume goes as diameter squared the volume would be a million times too small; re-enter the diameter in millimetres (90, not 0.09). ' +
        'A diameter outside the plausible aquaculture range is now flagged.',
    },
  },
  dosing: {
    version: 2,
    revisions: {
      2:
        'Calcium is now dosed as CaCO₃, not elemental Ca. Calcium doses from the previous version were about 2.5× too high. ' +
        'The pH mode no longer produces a dose figure — the relationship it used was not valid.',
    },
  },
  biofilter: {
    version: 3,
    revisions: {
      3:
        'TAN production is now derived from feed protein via Timmons & Ebeling eq. 3.9b (feed × protein% × 0.092) whenever a protein figure is entered, falling back to the 3% of feed rule of thumb when it is left blank. The two methods agree only near 32.6% protein: the 3% rule under-predicts TAN by about 7% at 35% protein, 27% at 45% and 35% at 50%, which is the salmonid and marine range. Results from the previous version used the 3% rule unconditionally and may therefore undersize nitrification for higher-protein feeds.',
      2:
        'Cold-water areal TAN removal corrected from 0.30 to 0.20 g/m²/day and moderate from 0.50 to 0.45, per Timmons & Ebeling Ch.8. ' +
        'Cold-water biofilters sized by the previous version are about 50% too small. A saltwater −30% derate was also added.',
    },
  },
  systemOxygenDemand: {
    version: 3,
    revisions: {
      3:
        'TAN production is now derived from feed protein via Timmons & Ebeling eq. 3.9b (feed × protein% × 0.092) whenever a protein figure is entered, falling back to the 3% of feed rule of thumb when it is left blank. The two methods agree only near 32.6% protein: the 3% rule under-predicts TAN by about 7% at 35% protein, 27% at 45% and 35% at 50%, which is the salmonid and marine range. Results from the previous version used the 3% rule unconditionally and may therefore undersize the nitrification component of the stoichiometric floor.',
      2:
        'Now sizes on the design basis of 1.0 kg O₂/kg feed rather than reporting the stoichiometric floor (~0.39) as total demand. ' +
        'Oxygen supply sized from the previous version may be undersized by up to 2.6×.',
    },
  },
  carryingCapacity: {
    version: 2,
    revisions: {
      2:
        'Design oxygen demand moved from 0.5 to 1.0 kg O₂/kg feed to match System O₂ Demand. ' +
        'Feed and biomass ceilings from the previous version are about twice what this basis allows.',
    },
  },
};

/** Current method version for a calculator. Unlisted calculators are v1. */
export const getCalcVersion = (calcId) => CALC_VERSIONS[calcId]?.version ?? 1;

/** Version an entry was computed at. Pre-stamping entries count as v1. */
export const getEntryVersion = (entry) => {
  const v = entry && entry.calcVersion;
  return Number.isFinite(v) ? v : 1;
};

/** True when the calculator's method has moved on since this entry was computed. */
export const isMethodStale = (entry) =>
  !!entry && getEntryVersion(entry) < getCalcVersion(entry.calcId);

/*
 * Inputs a saved entry was computed from that the current number reader reads
 * differently (numberInput.js). Before reader 2, "5.000" was read as 5 and
 * "5.000.000" as 5 even where the user meant thousands, so a figure saved from
 * one of those is a figure computed from the wrong number. Entries stamped
 * with inputReader >= 2 were read by the current rules and are never flagged.
 */
export const misreadInputs = (entry) => {
  if (!entry || (Number.isFinite(entry.inputReader) && entry.inputReader >= INPUT_READER_VERSION)) return [];
  const raw = entry.rawInputs;
  if (!raw || typeof raw !== 'object') return [];
  const out = [];
  for (const [key, v] of Object.entries(raw)) {
    if (typeof v !== 'string' || !/[.,]/.test(v)) continue;
    const was = legacyParseNumber(v);
    if (!Number.isFinite(was)) continue; // not a number then either
    const now = parseNumberInput(v);
    if (Number.isFinite(now) && Math.abs(now - was) <= 1e-9 * Math.max(1, Math.abs(was))) continue;
    out.push({ key, typed: v.trim(), was });
  }
  return out;
};

/** True when a saved figure should not be relied on: method changed, or an input was misread. */
export const isEntryStale = (entry) =>
  isMethodStale(entry) || misreadInputs(entry).length > 0;

/**
 * Every revision note between the entry's version and the current one, so an
 * entry two versions behind explains both changes rather than only the last.
 */
export const staleNotes = (entry) => {
  const notes = [];
  if (isMethodStale(entry)) {
    const revisions = CALC_VERSIONS[entry.calcId]?.revisions || {};
    const from = getEntryVersion(entry);
    const to = getCalcVersion(entry.calcId);
    for (let v = from + 1; v <= to; v++) {
      if (revisions[v]) notes.push(revisions[v]);
    }
  }
  return notes;
};

/*
 * The misread inputs as one language-neutral fragment ("5.000" → 5), for the
 * sentence the interface wraps around it (calc.misreadNote). Kept out of
 * staleNotes so the sentence itself can be translated.
 */
export const misreadValues = (entry) =>
  misreadInputs(entry)
    .map((m) => `"${m.typed}" → ${m.was}`)
    .join(', ');

/** Single-string form for compact surfaces. */
export const staleReason = (entry) => staleNotes(entry).join(' ');

/* ------------------------------------------------------------------ */
/* Revisions                                                           */
/* ------------------------------------------------------------------ */

/*
 * A saved result is superseded in two different ways, and the UI deliberately
 * uses the same word for both, because from the operator's point of view they
 * mean the same thing: there is a newer figure than this one.
 *
 *   by method  — the calculator itself was corrected (isEntryStale above).
 *                The old figure is WRONG; its numbers are withheld from Gia.
 *   by revision — the operator edited the inputs and saved the recalculation
 *                alongside. The old figure is still arithmetically correct for
 *                the inputs it was given, just no longer the current intent,
 *                so its numbers are kept and simply marked as superseded.
 *
 * An edited entry carries `supersedes: { id, ts, calcLabel }` naming the result
 * it revises, so the pair reads as a revision rather than a coincidental
 * near-duplicate sitting next to each other in the list.
 */

/** The entry this one revises, if it was produced by editing another. */
export const revisionSource = (entry) => (entry && entry.supersedes) || null;

/** The later revision of this entry, if one exists in the same project. */
export const findRevisionOf = (entry, allEntries) => {
  if (!entry || !entry.id) return null;
  return (allEntries || []).find(
    (e) => e && e.supersedes && e.supersedes.id === entry.id
  ) || null;
};
